/**
 * The customer and the address on the ticket, as in the prototype (renderCust, custCard, zoneLine):
 * pure functions, so the screen only renders.
 */
import type { CustomerDetail, DeliverySettings, LineRequest, Menu, Street, Zone } from "../../api/types";
import { t } from "../../i18n";
import { formatEuro, type Cents } from "../../lib/money";
import { formatPhone } from "../../lib/phone";
import type { Cart, Customer } from "./cart";

/** "9140 Temse", as the zone field shows a chosen zone. */
export const zoneText = (zone: Pick<Zone, "postcode" | "name">) => `${zone.postcode} ${zone.name}`;

/**
 * How a zone is named on the ticket, in the street list and in a customer's address: its name, or "2890
 * Puurs-Sint-Amands" when another zone has the same name with another postcode (Furkan, 20/10/2026; as the server).
 */
export function zoneLabel(zone: Pick<Zone, "postcode" | "name">, zones: readonly Pick<Zone, "postcode" | "name">[]) {
  const name = zone.name.trim().toLowerCase();
  const shared = zones.some((other) => other.postcode !== zone.postcode && other.name.trim().toLowerCase() === name);
  return shared ? zoneText(zone) : zone.name;
}

/** An active zone by its label, "postcode name" or a bare name (the first that is on). */
export function activeZone(settings: DeliverySettings | null, name: string): Zone | undefined {
  const asked = name.trim().toLowerCase();
  if (!asked || !settings) return undefined;
  const zones = settings.zones;
  const found =
    zones.find((zone) => zoneLabel(zone, zones).toLowerCase() === asked) ??
    zones.find((zone) => zoneText(zone).toLowerCase() === asked);
  if (found?.on) return found;
  return zones.find((zone) => zone.on && zone.name.toLowerCase() === asked);
}

/** The active zones that start with what was typed: the name, the postcode or "9140 Temse" (prototype zdraw). */
export function zoneSuggestions(settings: DeliverySettings | null, typed: string): Zone[] {
  const query = typed.trim().toLowerCase();
  return (settings?.zones ?? []).filter(
    (zone) =>
      zone.on &&
      (!query ||
        zone.name.toLowerCase().startsWith(query) ||
        zone.postcode.startsWith(query) ||
        zoneText(zone).toLowerCase().startsWith(query)),
  );
}

const MAX_STREETS = 8;

/** Lower case without accents: "Hôtel" and "hotel" are the same. */
const plain = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

/**
 * How well a street name fits what was typed: 0 it starts with it, 1 a word in it does ("roelens" →
 * "Adolf Roelenslaan"), 2 it is somewhere in it (from three letters: "laan"); null not at all.
 */
function fit(name: string, query: string): number | null {
  const street = plain(name);
  if (street.startsWith(query)) return 0;
  if (street.split(/[\s\-'.]+/).some((word) => word.startsWith(query))) return 1;
  return query.length >= 3 && street.includes(query) ? 2 : null;
}

/**
 * The streets that fit what was typed: those of the chosen zone first, then those of the other zones
 * with their zone next to them, so a street in another (deel)gemeente than the customer said is still
 * found (Papermoon, 08/10/2026: "Adolf Roelenslaan" asked as Temse lies in Kruibeke): from three letters,
 * or sooner when the chosen zone has none. Picking one of another zone takes its zone too. Accents and
 * case do not matter; at most 8.
 */
export function streetSuggestions(streets: readonly Street[], zone: string, typed: string): Street[] {
  const query = plain(typed.trim().replace(/\s+/g, " "));
  if (!query) return [];
  const inZone = plain(zone.trim());
  const ranked: { street: Street; rank: number; index: number }[] = [];
  streets.forEach((street, index) => {
    const how = fit(street.name, query);
    if (how === null) return;
    const other = inZone !== "" && plain(street.zone) !== inZone;
    ranked.push({ street, rank: (other ? 3 : 0) + how, index });
  });
  // short input with streets in the chosen zone: only those (one or two letters fit almost anything)
  const own = ranked.filter((item) => item.rank < 3);
  const shown = query.length < 3 && own.length > 0 ? own : ranked;
  return shown
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .slice(0, MAX_STREETS)
    .map((item) => item.street);
}

/** What the folded customer block still says (Furkan, 03/10/2026): who it is for, and for a delivery where to. */
export interface CustomerSummary {
  /** The name, else the phone number; "" when the ticket has neither. */
  who: string;
  /** The phone number next to the name; "" when the number is all there is. */
  phone: string;
  /** "Kasteelstraat 14 bus 2, 9140 Temse"; null for a takeaway, and for a delivery that still misses its address. */
  where: string | null;
  /** A delivery without a (deel)gemeente or a street: it cannot be ordered yet. */
  addressMissing: boolean;
}

export function customerSummary(
  cart: Pick<Cart, "channel" | "customer">,
  settings: DeliverySettings | null,
): CustomerSummary {
  const { name, phone, street, number, box, zone } = cart.customer;
  const who = name.trim() || phone.trim();
  const summary = { who, phone: name.trim() ? phone.trim() : "" };
  if (cart.channel !== "delivery") return { ...summary, where: null, addressMissing: false };
  if (zone.trim() === "" || street.trim() === "") return { ...summary, where: null, addressMissing: true };
  const chosen = activeZone(settings, zone);
  const door = `${street.trim()} ${number.trim()}${box.trim() ? ` ${t.customer.box.toLowerCase()} ${box.trim()}` : ""}`;
  return { ...summary, where: `${door.trim()}, ${chosen ? zoneText(chosen) : zone.trim()}`, addressMissing: false };
}

/** 4.1 → "4,1" (prototype kmIn). */
export const kmText = (km: number) => String(km).replace(".", ",");

/** The distance of a street of a zone, when known. */
export function streetKm(streets: readonly Street[], zone: string, street: string): number | null {
  const name = street.trim().toLowerCase();
  const inZone = zone.trim().toLowerCase();
  return streets.find((item) => item.zone.toLowerCase() === inZone && item.name.toLowerCase() === name)?.km ?? null;
}

export interface ZoneLine {
  text: string;
  /** "buiten leveringsgebied", in bold after the text. */
  outside: boolean;
}

/**
 * The line below the address (prototype zoneLine): "9140 Temse · minimum € 15,00 · levering € 0,50 ·
 * gratis vanaf € 50,00 · ± 4,1 km · toeslag afstand € 1,00 · buiten leveringsgebied". Null for a
 * zone that is not (or no longer) active.
 */
export function zoneLine(
  settings: DeliverySettings | null,
  zoneName: string,
  street: string,
  streets: readonly Street[],
): ZoneLine | null {
  const zone = activeZone(settings, zoneName);
  if (!settings || !zone) return null;
  const text = t.customer.zoneLine;
  const fee: Cents = zone.feeCents ?? settings.feeCents;
  const free = zone.freeFromCents ?? settings.freeFromCents;
  const km = streetKm(streets, zoneLabel(zone, settings.zones), street);
  const { fromKm, extraCents, maxKm } = zone.far;
  const extra = km !== null && fromKm !== null && extraCents !== null && km >= fromKm ? extraCents : 0;
  const parts = [
    zoneText(zone),
    text.minimum(formatEuro(zone.minimumCents)),
    text.fee(fee > 0 ? formatEuro(fee) : t.delivery.free),
    ...(free !== null ? [text.freeFrom(formatEuro(free))] : []),
    ...(km !== null ? [text.km(kmText(km))] : []),
    ...(extra > 0 ? [text.extra(formatEuro(extra))] : []),
  ];
  return { text: parts.join(" · "), outside: km !== null && maxKm !== null && km > maxKm };
}

/** "mrt 2024" (prototype: toLocaleDateString nl-BE, short month and year). */
export function sinceText(day: string): string {
  return new Date(`${day}T12:00:00`).toLocaleDateString("nl-BE", { month: "short", year: "numeric" });
}

/** "19 sep" for "Laatste (19 sep): …". */
export function lastOrderDay(at: string): string {
  return new Date(at).toLocaleDateString("nl-BE", { day: "numeric", month: "short" });
}

/** What the ticket shows of a known customer (prototype fillCustomer): every field from the customer. */
export function fromCustomer(customer: CustomerDetail): Customer {
  return {
    phone: formatPhone(customer.phone),
    name: customer.name,
    email: customer.email,
    street: customer.street,
    number: customer.number,
    box: customer.box,
    zone: customer.zone,
  };
}

/**
 * "Herhaal": the lines of the last order as they can be ordered now: without anything given for
 * free (that needs a code again), and without products or sizes that are no longer on the menu or are
 * sold out.
 */
export function repeatLines(
  lines: readonly LineRequest[],
  menu: Menu,
  soldOut: ReadonlySet<string>,
): { lines: LineRequest[]; skipped: number } {
  const kept: LineRequest[] = [];
  for (const line of lines) {
    const product = menu.products[line.productId];
    const sizeExists =
      line.variant === undefined || product?.variants.some((variant) => variant.label === line.variant) === true;
    if (!product || !sizeExists || soldOut.has(product.id)) continue;
    const { free: _free, promotion: _promotion, ...rest } = line;
    kept.push(rest);
  }
  return { lines: kept, skipped: lines.length - kept.length };
}
